import React, {useContext, useEffect, useState} from 'react';
import {Button, DatePicker, Form, Input, InputNumber, Select, Table} from "antd";
import {DeleteOutlined, PlusOutlined, InfoCircleOutlined} from "@ant-design/icons";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import {internalApi} from "../../../common/axios";
import {AuthContext} from "../../../shared/contexts/authContext";
import {useShopPackage, useShopPackageGroup} from "../../probability/useItemData";
import {PersonalPackageEntity} from "./PersonalPackageEntity";
import {PersonalPackageGroupEntity} from "./PersonalPackageGroupEntity";
import {useQueryClient} from "@tanstack/react-query";
import IsLoading from "../../../shared/components/IsLoading";

const SALE_TYPE = [
    {label: '0 (일반)', value: 0},
    {label: '1 (패키지 할인)', value: 1},
];

const PersonalPackageEditModal = ({record, handleClose}) => {
    const {currentUser} = useContext(AuthContext);
    const queryClient = useQueryClient();

    const {data: shopPackage, isLoading: isShopLoading} = useShopPackage();
    const {data: shopPackageGroup, isLoading: isShopGroupLoading} = useShopPackageGroup();

    const [row, setRow] = useState(new PersonalPackageEntity(record));
    const [groupItems, setGroupItems] = useState([]);
    const [groupLoading, setGroupLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (record.seq) {
            setGroupLoading(true);
            internalApi.get('store_manager/personalPackageGroup', {params: {groupIdx: record.seq}})
                .then(res => setGroupItems((res.data.rows || []).map(r => new PersonalPackageGroupEntity(r))))
                .catch(() => toast.error('그룹 데이터 로드 실패'))
                .finally(() => setGroupLoading(false));
        }
    }, [record.seq]);

    const handleSetRow = (key, value) => {
        setRow(prev => new PersonalPackageEntity({...prev, [key]: value}));
    };

    const handleAddGroupItem = () => {
        setGroupItems(prev => [
            ...prev,
            {...new PersonalPackageGroupEntity({groupIdx: row.seq}), _tempKey: Date.now()},
        ]);
    };

    const handleDeleteGroupItem = (index) => {
        setGroupItems(prev => prev.filter((_, i) => i !== index));
    };

    const handleGroupChange = (index, key, value) => {
        setGroupItems(prev => {
            const next = [...prev];
            next[index] = new PersonalPackageGroupEntity({...next[index], [key]: value});
            return next;
        });
    };

    // 패키지 이름 검색 Select용 옵션 생성
    const packageOptions = shopPackage && shopPackageGroup
        ? Object.entries(shopPackage[currentUser.target] || {}).map(([idx, pkg]) => {
            const groupItem = Object.values(shopPackageGroup[currentUser.target] || {})
                .find(g => g.groupIdx === pkg.groupIdx);
            const name = groupItem?.nameText || '';
            return {value: Number(idx), name, searchText: `${name} ${idx}`};
        }).filter(opt => opt.name)
        : [];

    const handlePackageSelect = (rowIndex, packageIndex, option) => {
        setGroupItems(prev => {
            const next = [...prev];
            next[rowIndex] = new PersonalPackageGroupEntity({
                ...next[rowIndex],
                packageIndex,
                packageName: option?.name || '',
            });
            return next;
        });
    };

    const hasAvrPaymentFilter = groupItems.some(item => item.minAvrPayment > 0 || item.maxAvrPayment > 0);
    const averagePaymentError = hasAvrPaymentFilter && !(row.averagePayment > 0);

    const groupDataSource = groupItems.map((item, i) => ({
        ...item,
        _avrError: item.minAvrPayment > 0 && (item.maxAvrPayment === 0 || item.minAvrPayment > item.maxAvrPayment),
        _levelError: item.minLevel > 0 && (item.maxLevel === 0 || item.minLevel > item.maxLevel),
        key: item.seq || item._tempKey || i,
    }));

    const handleSave = async () => {
        if (averagePaymentError) {
            toast.error('평균 결제 금액 조건 사용 시 평균 결제일을 입력해야 합니다.');
            return;
        }
        const rangeErrorIndex = groupDataSource.findIndex(item => item._avrError || item._levelError);
        if (rangeErrorIndex !== -1) {
            toast.error(`${rangeErrorIndex + 1}번째 행: 이상값은 미만값보다 작아야 합니다.`);
            return;
        }
        setSaving(true);
        try {
            const res = await internalApi.post('store_manager/personalPackage', {data: row.toJson()});
            const seq = res.data.seq ?? row.seq;
            await internalApi.post('store_manager/personalPackageGroup/bulk', {
                groupIdx: seq,
                items: groupItems.map(item => new PersonalPackageGroupEntity({...item, groupIdx: seq}).toJson()),
            });

            toast.success('저장 완료');
            queryClient.invalidateQueries({queryKey: ['personalPackage']});
            queryClient.invalidateQueries({queryKey: ['personalPackageGroup']});
            handleClose();
        } catch (e) {
            console.error(e);
            toast.error('저장 실패');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!row.seq) return;
        try {
            await internalApi.delete('store_manager/personalPackage', {params: {seq: row.seq}});
            toast.success('삭제 완료');
            queryClient.invalidateQueries({queryKey: ['personalPackage']});
            queryClient.invalidateQueries({queryKey: ['personalPackageGroup']});
            handleClose();
        } catch {
            toast.error('삭제 실패');
        }
    };

    const confirmDelete = () => {
        if (window.confirm('정말 삭제하시겠습니까? 관련 패키지 그룹도 함께 삭제됩니다.')) {
            handleDelete();
        }
    };

    const groupColumns = [
        {
            title: (
                <div>
                    <div>평균 결제 금액(달러)</div>
                    <div style={{fontSize: 11, color: '#888', fontWeight: 'normal'}}>이상 ~ 미만</div>
                </div>
            ),
            key: 'avrPayment',
            width: 200,
            render: (_, item, index) => {
                const err = item._avrError;
                return (
                    <div style={{display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'nowrap'}}>
                        <InputNumber
                            value={item.minAvrPayment}
                            size="small"
                            min={0}
                            step={0.01}
                            precision={2}
                            style={{width: 75}}
                            placeholder="min"
                            status={err ? 'error' : ''}
                            onChange={val => handleGroupChange(index, 'minAvrPayment', val)}
                        />
                        <span style={{fontSize: 11, color: err ? '#ff4d4f' : '#888', whiteSpace: 'nowrap'}}>이상 ~</span>
                        <InputNumber
                            value={item.maxAvrPayment}
                            size="small"
                            min={0}
                            step={0.01}
                            precision={2}
                            style={{width: 75}}
                            placeholder="max"
                            status={err ? 'error' : ''}
                            onChange={val => handleGroupChange(index, 'maxAvrPayment', val)}
                        />
                        <span style={{fontSize: 11, color: err ? '#ff4d4f' : '#888', whiteSpace: 'nowrap'}}>미만</span>
                    </div>
                );
            },
        },
        {
            title: (
                <div>
                    <div>레벨</div>
                    <div style={{fontSize: 11, color: '#888', fontWeight: 'normal'}}>이상 ~ 미만</div>
                </div>
            ),
            key: 'level',
            width: 200,
            render: (_, item, index) => {
                const err = item._levelError;
                return (
                    <div style={{display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'nowrap'}}>
                        <InputNumber
                            value={item.minLevel}
                            size="small"
                            min={0}
                            style={{width: 60}}
                            placeholder="min"
                            status={err ? 'error' : ''}
                            onChange={val => handleGroupChange(index, 'minLevel', val)}
                        />
                        <span style={{fontSize: 11, color: err ? '#ff4d4f' : '#888', whiteSpace: 'nowrap'}}>이상 ~</span>
                        <InputNumber
                            value={item.maxLevel}
                            size="small"
                            min={0}
                            style={{width: 60}}
                            placeholder="max"
                            status={err ? 'error' : ''}
                            onChange={val => handleGroupChange(index, 'maxLevel', val)}
                        />
                        <span style={{fontSize: 11, color: err ? '#ff4d4f' : '#888', whiteSpace: 'nowrap'}}>미만</span>
                    </div>
                );
            },
        },
        {
            title: '할인',
            dataIndex: 'saleType',
            key: 'saleType',
            width: 180,
            render: (v, _, index) => (
                <Select
                    value={v}
                    size="small"
                    style={{width: '100%'}}
                    onChange={val => handleGroupChange(index, 'saleType', val)}
                >
                    {SALE_TYPE.map(opt => (
                        <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                    ))}
                </Select>
            ),
        },
        {
            title: '패키지',
            key: 'package',
            render: (_, item, index) => (
                <Select
                    value={item.packageIndex || undefined}
                    size="small"
                    style={{width: '100%'}}
                    showSearch
                    allowClear
                    placeholder="패키지 이름으로 검색"
                    optionFilterProp="searchText"
                    filterOption={(input, option) =>
                        (option?.searchText ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(val, opt) => handlePackageSelect(index, val, opt)}
                    options={packageOptions.map(opt => ({
                        value: opt.value,
                        name: opt.name,
                        label: (
                            <span>
                                <span style={{color: '#999', fontSize: 11, marginRight: 6}}>[{opt.value}]</span>
                                {opt.name}
                            </span>
                        ),
                        searchText: opt.searchText,
                    }))}
                />
            ),
        },
        {
            title: '',
            key: 'del',
            width: 48,
            render: (_, __, index) => (
                <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined/>}
                    onClick={() => handleDeleteGroupItem(index)}
                />
            ),
        },
    ];

    if (isShopLoading || isShopGroupLoading) return <IsLoading/>;

    return (
        <div>
            <Form layout="inline" style={{marginBottom: 8}}>
                <Form.Item label="메모" style={{width: '100%', marginBottom: 8}}>
                    <Input
                        value={row.memo}
                        size="small"
                        onChange={e => handleSetRow('memo', e.target.value)}
                    />
                </Form.Item>
                <Form.Item
                    label="마지막 결제일"
                    tooltip={{
                        title: '0이면 모두',
                        icon: <InfoCircleOutlined style={{color: '#1890ff'}}/>,
                    }}
                    style={{marginBottom: 8, width: '45%'}}
                >
                    <InputNumber
                        value={row.lastPayment}
                        size="small"
                        min={0}
                        addonAfter="일"
                        style={{width: '100%'}}
                        onChange={v => handleSetRow('lastPayment', v)}
                    />
                </Form.Item>
                <Form.Item
                    label="평균 결제일"
                    tooltip={{
                        title: '평균 결제 금액 = 평균 결제일 내 총 금액 / 평균 결제일',
                        icon: <InfoCircleOutlined style={{color: '#1890ff'}}/>,
                    }}
                    validateStatus={averagePaymentError ? 'error' : ''}
                    help={averagePaymentError ? '평균 결제 금액 조건 사용 시 필수 입력' : ''}
                    style={{marginBottom: 8, width: '45%'}}
                >
                    <InputNumber
                        value={row.averagePayment}
                        size="small"
                        min={0}
                        addonAfter="일"
                        style={{width: '100%'}}
                        status={averagePaymentError ? 'error' : ''}
                        onChange={v => handleSetRow('averagePayment', v)}
                    />
                </Form.Item>
                <Form.Item label="결제 횟수(이상)" style={{marginBottom: 8, width: '45%'}}>
                    <InputNumber
                        value={row.paymentCount}
                        size="small"
                        min={0}
                        style={{width: '100%'}}
                        onChange={v => handleSetRow('paymentCount', v)}
                    />
                </Form.Item>
                <Form.Item
                    label="누적 기준일"
                    tooltip={{
                        title: '누적 로그인, 누적 결제 금액의 기준일',
                        icon: <InfoCircleOutlined style={{color: '#1890ff'}}/>,
                    }}
                    style={{marginBottom: 8, width: '45%'}}
                >
                    <DatePicker
                        value={row.baseDate ? dayjs(row.baseDate) : null}
                        size="small"
                        style={{width: '100%'}}
                        showTime={{format: 'HH:mm'}}
                        format="YYYY-MM-DD HH:mm"
                        onChange={(_, dateStr) => handleSetRow('baseDate', dateStr)}
                    />
                </Form.Item>
                <Form.Item label="누적 로그인 일수(이상)" style={{marginBottom: 8, width: '45%'}}>
                    <InputNumber
                        value={row.cumulativeLoginCount}
                        size="small"
                        min={0}
                        style={{width: '100%'}}
                        onChange={v => handleSetRow('cumulativeLoginCount', v)}
                    />
                </Form.Item>
                <Form.Item label="누적 결제 금액(달러)" style={{marginBottom: 8, width: '45%'}}>
                    <div style={{display: 'flex', gap: 4}}>
                        <InputNumber
                            value={row.cumulativePayment}
                            size="small"
                            min={0}
                            style={{flex: 1}}
                            onChange={v => handleSetRow('cumulativePayment', v)}
                        />
                        <Select
                            value={row.isCumPaymentOver}
                            size="small"
                            style={{width: 80}}
                            onChange={v => handleSetRow('isCumPaymentOver', v)}
                        >
                            <Select.Option value={1}>이상▲</Select.Option>
                            <Select.Option value={0}>이하▼</Select.Option>
                        </Select>
                    </div>
                </Form.Item>
            </Form>

            <div style={{
                border: '2px solid #1e4d7b',
                borderRadius: 8,
                padding: 12,
                marginTop: 8
            }}>
                <Table
                    columns={groupColumns}
                    dataSource={groupDataSource}
                    pagination={false}
                    size="small"
                    loading={groupLoading}
                    scroll={{x: 'max-content'}}
                />
                <Button
                    style={{marginTop: 8}}
                    icon={<PlusOutlined/>}
                    onClick={handleAddGroupItem}
                    size="small"
                >
                    추가하기
                </Button>
            </div>

            <div style={{marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
                {row.seq > 0 && (
                    <Button danger onClick={confirmDelete}>삭제</Button>
                )}
                <Button onClick={handleClose}>취소</Button>
                <Button type="primary" loading={saving} onClick={handleSave}>저장</Button>
            </div>
        </div>
    );
};

export default PersonalPackageEditModal;
